import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Mail, Database, Wifi, WifiOff, CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { api } from '../../lib/api';

const COOLDOWN_MINUTES = 5;
const LAST_BACKUP_KEY = 'lastDatabaseBackupTimestamp';

export default function DatabaseBackupSettings() {
  const { t } = useLanguage();
  const TARGET_EMAIL = 'safeenazad197@gmail.com';
  const [loading, setLoading] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [canSendBackup, setCanSendBackup] = useState(true);

  // Check cooldown status on mount and update periodically
  useEffect(() => {
    const checkCooldown = () => {
      const lastBackupTimestamp = localStorage.getItem(LAST_BACKUP_KEY);
      if (lastBackupTimestamp) {
        const lastBackupTime = parseInt(lastBackupTimestamp, 10);
        const now = Date.now();
        const fiveMinutesInMs = COOLDOWN_MINUTES * 60 * 1000;
        const timeSinceLastBackup = now - lastBackupTime;
        
        if (timeSinceLastBackup < fiveMinutesInMs) {
          setCanSendBackup(false);
        } else {
          setCanSendBackup(true);
        }
      } else {
        setCanSendBackup(true);
      }
    };

    checkCooldown();
    // Check every second to update the status
    const interval = setInterval(checkCooldown, 1000);

    return () => clearInterval(interval);
  }, []);

  // Automatic internet connection check every 10 seconds
  useEffect(() => {
    const checkConnectionAuto = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        await fetch('https://www.google.com/favicon.ico', {
          method: 'HEAD',
          mode: 'no-cors',
          signal: controller.signal,
          cache: 'no-cache'
        });
        
        clearTimeout(timeoutId);
        setIsOnline(true);
      } catch (error) {
        setIsOnline(false);
      }
    };

    // Check immediately on mount
    checkConnectionAuto();
    
    // Then check every 10 seconds
    const connectionInterval = setInterval(checkConnectionAuto, 10000);

    return () => clearInterval(connectionInterval);
  }, []);

  const checkInternetConnection = async () => {
    setCheckingConnection(true);
    setIsOnline(null);
    
    try {
      // Try to fetch a small file from a reliable server
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
        cache: 'no-cache'
      });
      
      clearTimeout(timeoutId);
      setIsOnline(true);
      setMessage(null);
    } catch (error) {
      setIsOnline(false);
      setMessage({
        type: 'error',
        text: t('settings.databaseBackup.noConnection')
      });
    } finally {
      setCheckingConnection(false);
    }
  };

  const handleSendDatabase = async () => {
    // Check connection first
    setCheckingConnection(true);
    setIsOnline(null);
    
    let onlineStatus = false;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
        cache: 'no-cache'
      });
      
      clearTimeout(timeoutId);
      onlineStatus = true;
      setIsOnline(true);
    } catch (error) {
      onlineStatus = false;
      setIsOnline(false);
      setMessage({
        type: 'error',
        text: t('settings.databaseBackup.noConnection')
      });
      setCheckingConnection(false);
      return;
    } finally {
      setCheckingConnection(false);
    }

    if (!onlineStatus) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await api.post('/api/database/send-backup', { email: TARGET_EMAIL });
      localStorage.setItem(LAST_BACKUP_KEY, Date.now().toString());
      setCanSendBackup(false);
      setMessage({
        type: 'success',
        text: t('settings.databaseBackup.success')
      });
    } catch (error: any) {
      console.error('Error sending database:', error);
      setMessage({
        type: 'error',
        text: error.message?.includes('Failed to fetch') || error.message?.includes('Unauthorized')
          ? (error.message?.includes('Unauthorized') ? t('settings.databaseBackup.error') : t('settings.databaseBackup.noConnection'))
          : (error.message || t('settings.databaseBackup.error'))
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('settings.back')}
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('settings.databaseBackup.title')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">{t('settings.databaseBackup.subtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              {t('settings.databaseBackup.cardTitle')}
            </CardTitle>
            <CardDescription>
              {t('settings.databaseBackup.cardDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Internet Connection Status */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">
                  {t('settings.databaseBackup.connectionStatus')}
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={checkInternetConnection}
                  disabled={checkingConnection}
                >
                  {checkingConnection ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('settings.databaseBackup.checking')}
                    </>
                  ) : (
                    <>
                      {isOnline === null && <Wifi className="h-4 w-4 mr-2" />}
                      {isOnline === true && <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />}
                      {isOnline === false && <XCircle className="h-4 w-4 mr-2 text-red-500" />}
                      {isOnline === null ? t('settings.databaseBackup.checkConnection') : (isOnline ? t('settings.databaseBackup.connected') : t('settings.databaseBackup.disconnected'))}
                    </>
                  )}
                </Button>
              </div>
              {isOnline !== null && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                  isOnline ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {isOnline ? (
                    <>
                      <Wifi className="h-5 w-5" />
                      <span>{t('settings.databaseBackup.connectedMessage')}</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="h-5 w-5" />
                      <span>{t('settings.databaseBackup.disconnectedMessage')}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Email Display */}
            <div className="space-y-2">
              <Label className="text-base font-medium">
                {t('settings.databaseBackup.emailLabel')}
              </Label>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-base font-mono text-slate-700">{TARGET_EMAIL}</p>
              </div>
              <p className="text-sm text-slate-500">
                {t('settings.databaseBackup.emailHint')}
              </p>
            </div>

            {/* Message Alert */}
            {message && (
              <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                  {message.text}
                </AlertDescription>
              </Alert>
            )}

            {/* Cooldown Message */}
            {!canSendBackup && (
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertDescription className="text-yellow-800 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {t('settings.databaseBackup.cooldownMessage')}
                </AlertDescription>
              </Alert>
            )}

            {/* Send Button */}
            <Button
              onClick={handleSendDatabase}
              disabled={loading || checkingConnection || isOnline === false || !canSendBackup}
              className="w-full bg-slate-900 hover:bg-slate-800"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t('settings.databaseBackup.sending')}
                </>
              ) : (
                <>
                  <Mail className="h-5 w-5 mr-2" />
                  {t('settings.databaseBackup.sendButton')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

